import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { MOVE_TYPES } from '../data/mockData';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const AddMoveModal = ({ isOpen, onClose, onAddMove, editingMove }) => {
  const { toast } = useToast();
  const { folders } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    videoLink: '',
    origin: '',
    description: '',
    targetFolder: '',
    targetSubfolder: ''
  });

  // Ajout d'un hook pour détecter le thème
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.body.classList.contains('light') ? 'light' : 'dark';
    }
    return 'dark';
  });
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTheme(document.body.classList.contains('light') ? 'light' : 'dark');
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Initialize form when editing
  useEffect(() => {
    if (editingMove) {
      setFormData({
        name: editingMove.name,
        type: editingMove.type,
        videoLink: editingMove.videoLink || '',
        origin: editingMove.origin || '',
        description: editingMove.description || '',
        targetFolder: editingMove.targetFolder || '',
        targetSubfolder: editingMove.targetSubfolder || ''
      });
    } else {
      setFormData({
        name: '',
        type: '',
        videoLink: '',
        origin: '',
        description: '',
        targetFolder: '',
        targetSubfolder: ''
      });
    }
  }, [editingMove]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.type) {
      toast({
        title: "Missing Information",
        description: "Please fill in the move name and type.",
        variant: "destructive"
      });
      return;
    }

    const newMove = {
      id: editingMove ? editingMove.id : Date.now().toString(),
      ...formData
    };

    onAddMove(newMove);
    
    toast({
      title: editingMove ? "Move Updated! 🎉" : "Move Added! 🎉",
      description: `${formData.name} has been ${editingMove ? 'updated' : 'added'} to the encyclopedia.`
    });

    // Reset form
    setFormData({
      name: '',
      type: '',
      videoLink: '',
      origin: '',
      description: '',
      targetFolder: '',
      targetSubfolder: ''
    });
    
    onClose();
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`sm:max-w-md ${theme === 'light' ? 'bg-white border-black text-black' : 'bg-black border-white text-white'}`}>
        <DialogHeader>
          <DialogTitle className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {editingMove ? 'Edit Move' : 'Add New Move'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Move Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Cross Step"
              className={`${theme === 'light' ? 'bg-white text-black border-black' : 'bg-black text-white border-white'} focus:ring-2 focus:ring-blue-500 transition-all duration-200`}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="type">Move Type *</Label>
            <Select value={formData.type} onValueChange={(value) => handleChange('type', value)}>
              <SelectTrigger className={`${theme === 'light' ? 'bg-white text-black border-black' : 'bg-black text-white border-white'} focus:ring-2 focus:ring-blue-500 transition-all duration-200`}>
                <SelectValue placeholder="Select move type" />
              </SelectTrigger>
              <SelectContent>
                {MOVE_TYPES.map(type => (
                  <SelectItem key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="videoLink">Video Link</Label>
            <Input
              id="videoLink"
              value={formData.videoLink}
              onChange={(e) => handleChange('videoLink', e.target.value)}
              placeholder="https://youtube.com/..."
              className={`${theme === 'light' ? 'bg-white text-black border-black' : 'bg-black text-white border-white'} focus:ring-2 focus:ring-blue-500 transition-all duration-200`}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="origin">Origin</Label>
            <Input
              id="origin"
              value={formData.origin}
              onChange={(e) => handleChange('origin', e.target.value)}
              placeholder="e.g., Classic breaking foundation"
              className={`${theme === 'light' ? 'bg-white text-black border-black' : 'bg-black text-white border-white'} focus:ring-2 focus:ring-blue-500 transition-all duration-200`}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe the move..."
              rows={3}
              className={`${theme === 'light' ? 'bg-white text-black border-black' : 'bg-black text-white border-white'} focus:ring-2 focus:ring-blue-500 transition-all duration-200 resize-none`}
            />
          </div>

          <div className="space-y-2">
            <Label>Target Folder (Optional)</Label>
            <div className="grid grid-cols-2 gap-2">
              <Select value={formData.targetFolder} onValueChange={(value) => handleChange('targetFolder', value)}>
                <SelectTrigger className={`${theme === 'light' ? 'bg-white text-black border-black' : 'bg-black text-white border-white'} focus:ring-2 focus:ring-blue-500 transition-all duration-200`}>
                  <SelectValue placeholder="Select folder" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(folders || {}).map(([folderKey, folder]) => (
                    <SelectItem key={folderKey} value={folderKey}>
                      {folder.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select 
                value={formData.targetSubfolder} 
                onValueChange={(value) => handleChange('targetSubfolder', value)}
                disabled={!formData.targetFolder}
              >
                <SelectTrigger className={`${theme === 'light' ? 'bg-white text-black border-black' : 'bg-black text-white border-white'} focus:ring-2 focus:ring-blue-500 transition-all duration-200`}>
                  <SelectValue placeholder="Select subfolder" />
                </SelectTrigger>
                <SelectContent>
                  {formData.targetFolder && folders?.[formData.targetFolder]?.children && 
                    Object.entries(folders[formData.targetFolder].children).map(([subfolderKey, subfolder]) => (
                      <SelectItem key={subfolderKey} value={subfolderKey}>
                        {subfolder.name}
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 hover:bg-gray-50 transition-all duration-200"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200"
            >
              {editingMove ? 'Update Move' : 'Add Move'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddMoveModal;